import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { IITH } from '@/pages/landing/iithBrand';

const MODES = [
  {
    value: 'slots',
    title: 'Time slots',
    hint: 'Patients pick a clock time. Slot length comes from clinic (or doctor) setting.',
  },
  {
    value: 'daily_count',
    title: 'Appointments per day',
    hint: 'Patients request a place for the day up to the daily limit — no time grid.',
  },
];

/** Clinic-wide patient booking rules form. */
export default function ClinicBookingConfigForm({ form, setForm }) {
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="space-y-6" data-testid="clinic-booking-config-form">
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Booking style</Label>
        <div className="mt-2 grid sm:grid-cols-2 gap-3">
          {MODES.map((m) => {
            const on = form.booking_mode === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => set('booking_mode', m.value)}
                className={`text-left rounded-xl border p-4 transition ${
                  on ? 'border-[#F15A22] bg-[#FFF7F3] shadow-sm' : 'border-zinc-200 bg-white hover:border-zinc-300'
                }`}
                data-testid={`booking-mode-${m.value}`}
              >
                <p className="text-sm font-semibold text-[#071B34]">{m.title}</p>
                <p className="text-[12px] text-zinc-500 mt-1 leading-relaxed">{m.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {form.booking_mode === 'slots' ? (
          <div className="space-y-1.5">
            <Label htmlFor="slot_minutes">Minutes per slot</Label>
            <Input
              id="slot_minutes"
              type="number"
              min={5}
              max={120}
              value={form.slot_minutes}
              onChange={(e) => set('slot_minutes', Number(e.target.value))}
              data-testid="booking-slot-minutes"
            />
          </div>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="max_day">Max appointments per doctor / day</Label>
          <Input
            id="max_day"
            type="number"
            min={1}
            max={500}
            value={form.max_appointments_per_day}
            onChange={(e) => set('max_appointments_per_day', Number(e.target.value))}
            data-testid="booking-max-day"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="max_walkins">Max walk-ins per doctor / day</Label>
          <Input
            id="max_walkins"
            type="number"
            min={0}
            max={500}
            value={form.max_walkins_per_day ?? 0}
            onChange={(e) => set('max_walkins_per_day', Number(e.target.value))}
            data-testid="booking-max-walkins"
          />
          <p className="text-[11px] text-zinc-400">
            Reception desk walk-in registrations. <strong>0 = unlimited</strong>.
            Override per doctor on their profile, or per day on the roster.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-[#D5DEE8] bg-white p-4 space-y-4">
        <div>
          <p className="text-sm font-semibold text-[#071B34]">Patient timing rules</p>
          <p className="text-[12px] text-zinc-500 mt-0.5">
            Applies to online / patient portal requests only (not reception walk-ins).
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="book_prior">Hours before appointment to book</Label>
            <Input
              id="book_prior"
              type="number"
              min={0}
              max={168}
              value={form.patient_book_hours_prior}
              onChange={(e) => set('patient_book_hours_prior', Number(e.target.value))}
              data-testid="booking-book-prior"
            />
            <p className="text-[11px] text-zinc-400">
              e.g. 2 → patients cannot book a slot starting in under 2 hours.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cancel_prior">Hours before appointment to cancel</Label>
            <Input
              id="cancel_prior"
              type="number"
              min={0}
              max={168}
              value={form.patient_cancel_hours_prior}
              onChange={(e) => set('patient_cancel_hours_prior', Number(e.target.value))}
              data-testid="booking-cancel-prior"
            />
            <p className="text-[11px] text-zinc-400">
              Patient self-cancel cutoff (clinic staff can still cancel anytime).
            </p>
          </div>
        </div>
        <p className="text-[11px]" style={{ color: IITH.mute }}>
          Defaults apply to every doctor. Override per doctor on their profile under Appointments.
        </p>
      </div>
    </div>
  );
}
